import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, CloudDownload, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ImageSettings } from '@/components/image-lightbox/ImageDetail/components/ImageSettings';
import { Separator } from '@/components/ui/separator';
import GradientButton from '@/components/ui/GradientButton';
import StorageButton from '@/components/universal-storage/StorageButton';
import AddToProjectButton from './AddToProjectButton';
import { PrivateImageGeneratorItem } from '@/hooks/private-image-generator/types';
import { cn } from '@/lib/utils';

interface PrivateImageGeneratorSidebarProps {
  image: PrivateImageGeneratorItem;
  isOwner?: boolean; // Always true for private generator, but keeping for consistency
  isDeleteLoading: boolean;
  onDownload: () => void;
  onDelete: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onLoadMore?: () => void;
}

export const PrivateImageGeneratorSidebar: React.FC<PrivateImageGeneratorSidebarProps> = ({
  image,
  isOwner = true, // Default to true for private images (they belong to the current user)
  isDeleteLoading,
  onDownload,
  onDelete,
  hasPrevious = false,
  hasNext = false,
  isLoadingMore = false,
  hasNextPage = false,
  onPrevious,
  onNext,
  onLoadMore
}) => {
  const location = useLocation();
  const isStoragePage = location.pathname.includes('/storage');

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Sticky Navigation Section */}
      <div className="sticky top-0 z-20 bg-background">
        {/* Navigation arrows */}
        <div className="border-b border-accent/10 bg-background">
          <div className="flex items-center justify-between p-3 md:p-4">
            <motion.button
              onClick={onPrevious}
              disabled={!hasPrevious}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
                "bg-white/80 dark:bg-gray-800/80 border border-purple-200/50 dark:border-purple-700/50",
                "hover:bg-white/90 dark:hover:bg-gray-700/90 hover:shadow-sm",
                !hasPrevious && "opacity-50 cursor-not-allowed"
              )}
            >
              <ChevronLeft size={16} className="flex-shrink-0" />
              <span className="text-sm font-medium">Previous</span>
            </motion.button>

            <motion.button
              onClick={hasNext ? onNext : (hasNextPage ? onLoadMore : undefined)}
              disabled={!hasNext && !hasNextPage}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
                "bg-white/80 dark:bg-gray-800/80 border border-purple-200/50 dark:border-purple-700/50",
                "hover:bg-white/90 dark:hover:bg-gray-700/90 hover:shadow-sm",
                !hasNext && !hasNextPage && "opacity-50 cursor-not-allowed"
              )}
            >
              {isLoadingMore ? (
                <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent animate-pulse">
                  Loading...
                </span>
              ) : (
                <>
                  <span>Next</span>
                  <ChevronRight size={16} className="flex-shrink-0" />
                </>
              )}
            </motion.button>
          </div>
        </div>

        {/* Storage Button - Only show on storage pages, not on private generator pages */}
        {isStoragePage && (
          <div className="bg-background">
            <div className="px-3 md:px-4 pt-3 md:pt-4 pb-2 md:pb-2">
              <StorageButton 
                itemId={image.id}
                contentType="private_image"
                variant="compact"
                gradientFrom="from-blue-50/80 to-teal-50/80 dark:from-blue-950/30 dark:to-teal-950/30"
                gradientTo="hover:from-blue-100/80 hover:to-teal-100/80 dark:hover:from-blue-900/40 dark:hover:to-teal-900/40"
                accentColor="from-blue-400 to-teal-500"
                iconColor="text-blue-600 dark:text-blue-400"
                borderColor="border-blue-200/60 dark:border-blue-700/40"
                hoverBorderColor="hover:border-blue-300/70 dark:hover:border-blue-600/50"
                addText="Save Image"
                manageText="Manage Image"
                addDescription="Add to your storage"
                manageDescription="Image saved"
                dropdownLabel="Save to Storage"
              />
            </div>
          </div>
        )}

        {/* Add to Project Button - Uses Private Image Generator hook and data */}
        <div className="border-b border-accent/10 bg-background">
          <div className="px-3 md:px-4 pt-2 md:pt-2 pb-3 md:pb-4">
            <AddToProjectButton 
              itemId={image.id}
              dropdownLabel="Add to Project"
              variant="compact"
              contentType="private_image"
              gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
              gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
              accentColor="from-purple-400 to-violet-500"
              iconColor="text-purple-600 dark:text-purple-400"
              borderColor="border-purple-200/60 dark:border-purple-700/40"
              hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
              addText="Add to Project"
              manageText="Manage Project"
              addDescription="Assign to a project"
              manageDescription="Project assigned"
            />
          </div>
        </div>

        {/* Scrollable content area - NO FilterInfoDisplay for private images */}
        <div className="p-3 md:p-4 md:overflow-y-auto">
          {/* Settings section - directly without filter display */}
          <div>
            <ImageSettings image={image as any} />
          </div>
          
          <Separator className="my-4" />
          
          {/* Delete button only */}
          <div className="mt-4">
            {isOwner && (
              <GradientButton
                variant="red"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onDelete}
                isLoading={isDeleteLoading}
                disabled={isDeleteLoading}
                testId="delete-image-button"
                fullWidth
              >
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </GradientButton>
            )}
          </div>
        </div>
        
        {/* Fixed bottom section with download button */}
        <div className="border-t border-accent/10 bg-background md:mt-auto">
          <div className="p-3 md:p-4">
            <GradientButton
              variant="blue"
              icon={<CloudDownload className="h-4 w-4" />}
              onClick={onDownload}
              testId="download-image-button"
              fullWidth
            >
              Download
            </GradientButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateImageGeneratorSidebar; 